import { weth9 } from "../contracts/artifacts";
import { PERMIT2_ABI, PERMIT2_BYTECODE } from "../contracts/tokens/permit2";

import { deployContract } from "./deploy-utils";

import type { DeployContext } from "./deploy-utils";
import type { DeploymentPhaseResult, FoundationAddresses } from "./types";
import type { Address, Hex } from "viem";

/**
 * Canonical Permit2 address used across all EVM chains.
 * We deploy the real Permit2 contract and then move its code to the
 * canonical address using `anvil_setCode` so that contracts expecting
 * Permit2 at this address work correctly.
 */
export const PERMIT2_ADDRESS =
  "0x000000000022D473030F116dDEE9F6B43aC78BA3" as Address;

/**
 * Phase 1: Deploy foundation contracts.
 *
 * Deploys WETH9 (canonical wrapped ether) and Permit2 (real compiled
 * contract placed at its canonical address).
 */
export async function deployFoundation(
  ctx: DeployContext,
): Promise<DeploymentPhaseResult<FoundationAddresses>> {
  const start = Date.now();
  let totalGas = 0n;
  const txHashes: `0x${string}`[] = [];

  // 1. Deploy WETH9
  const weth9Artifact = weth9();
  const weth9Result = await deployContract(
    ctx.publicClient,
    ctx.walletClient,
    ctx.deployer,
    weth9Artifact,
  );
  totalGas += weth9Result.gasUsed;
  txHashes.push(weth9Result.txHash);

  // 2. Deploy real Permit2 contract, then move its runtime code to the
  //    canonical address so that other contracts can reference it at
  //    the well-known 0x000...22D4 address.
  const permit2Result = await deployContract(
    ctx.publicClient,
    ctx.walletClient,
    ctx.deployer,
    { abi: PERMIT2_ABI, bytecode: PERMIT2_BYTECODE as Hex },
  );
  totalGas += permit2Result.gasUsed;
  txHashes.push(permit2Result.txHash);

  // Copy the deployed runtime bytecode to the canonical Permit2 address
  const permit2RuntimeCode = await ctx.publicClient.getCode({
    address: permit2Result.address,
  });
  if (permit2RuntimeCode) {
    await ctx.request({
      method: "anvil_setCode",
      params: [PERMIT2_ADDRESS, permit2RuntimeCode],
    });
  }

  return {
    phase: 1,
    name: "Foundation",
    addresses: {
      weth9: weth9Result.address,
      permit2: PERMIT2_ADDRESS,
    },
    gasUsed: totalGas,
    durationMs: Date.now() - start,
    txHashes,
  };
}
